import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/axios';
import { getDiagnosticProgress, getHomeTopicProgress, getDiagnosticFullReport, getHomePrePostComparison } from '../api/endpoints';
import { parsePlan } from '../lib/utils';
import {
  BookOpen, Target, TrendingUp, Award, Flame, Clock, ChevronRight,
  Play, FileText, Brain, ArrowUpRight, ArrowDownRight, Zap, Trophy,
  ClipboardList, CheckCircle
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [diagnosticProgress, setDiagnosticProgress] = useState(null);
  const [topicProgress, setTopicProgress] = useState([]);
  const [improvementPredictions, setImprovementPredictions] = useState(null);
  const [prePostData, setPrePostData] = useState(null);
  const [stats, setStats] = useState({
    questionsAnswered: 0,
    accuracy: 0,
    streak: 0,
    topicsMastered: 0
  });
  const { tenant, getPrimaryColor } = useTenant();

  const tenantName = tenant?.name || 'MedMCQ';
  const primaryColor = getPrimaryColor() || '#0891b2';

  useEffect(() => {
    document.title = `Dashboard | ${tenantName}`;
  }, [tenantName]);

  useEffect(() => {
    loadSubscription();
    loadStats();
    loadDiagnosticProgress();
    loadTopicProgress();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await api.get('/user/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setSubscription({ subscription_plan: 'free' });
    }
  };

  const loadDiagnosticProgress = async () => {
    try {
      const res = await getDiagnosticProgress();
      setDiagnosticProgress(res.data);
      if (res.data?.all_complete) {
        loadImprovementPredictions();
        loadPrePostComparison();
      }
    } catch (e) {
      // silently ignore — banner is non-critical
    }
  };

  const loadImprovementPredictions = async () => {
    try {
      const res = await getDiagnosticFullReport();
      if (res.data?.improvement_predictions) {
        setImprovementPredictions(res.data.improvement_predictions);
      }
    } catch (e) {
      // silently ignore — predictions are non-critical
    }
  };

  const loadPrePostComparison = async () => {
    try {
      const res = await getHomePrePostComparison();
      if (res.data?.has_data) {
        setPrePostData(res.data);
      }
    } catch (e) {
      // silently ignore — comparison is non-critical
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/user/stats');
      setStats({
        questionsAnswered: response.data.total_answered || 0,
        accuracy: response.data.overall_accuracy || 0,
        streak: response.data.current_streak || 0,
        topicsMastered: response.data.topics_mastered || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const TOPIC_COLOURS = [
    '#06b6d4', '#a855f7', '#ef4444', '#22c55e', '#f59e0b',
    '#3b82f6', '#14b8a6', '#ec4899', '#6366f1', '#f97316',
  ];

  const loadTopicProgress = async () => {
    try {
      const res = await getHomeTopicProgress();
      const topics = (res.data.topics || [])
        .filter(t => t.attempted > 0)  // Only show categories with answers
        .map((t) => {
          const acc = t.accuracy_pct ?? 0;
          // Color by accuracy level: green ≥70%, amber 50-69%, red <50%
          const color = acc >= 70 ? '#10b981' : acc >= 50 ? '#f59e0b' : '#ef4444';
          return {
            name: t.category,
            progress: t.percentage,
            accuracy: acc,
            correct: t.correct ?? 0,
            attempted: t.attempted,
            total: t.total,
            color,
          };
        })
        .sort((a, b) => b.accuracy - a.accuracy)  // Highest accuracy first
        .slice(0, 10);  // Top 10 topics only
      setTopicProgress(topics);
    } catch (e) {
      console.error('Error loading topic progress:', e);
    }
  };

  const handleInitSampleData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/init/comprehensive-sample-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      alert(data.message || 'Sample questions loaded successfully!');
    } catch (error) {
      console.error('Error initializing sample data:', error);
      alert('Sample questions loaded!');
    } finally {
      setLoading(false);
    }
  };

  const getFirstName = () => {
    const name = user?.full_name || user?.email?.split('@')[0] || 'Student';
    return name.split(' ')[0];
  };

  const quickActions = [
    { label: 'Practice Questions', icon: BookOpen, colour: 'blue', path: '/questions' },
    { label: 'Start Exam', icon: Target, colour: 'green', path: '/exam' },
    { label: 'AI Assistant', icon: Brain, colour: 'turquoise', path: '/ai' },
    { label: 'View Analytics', icon: TrendingUp, colour: 'turquoise', path: '/analytics' },
  ];

  const recentActivity = [
    { action: 'Completed', detail: 'Cardiology Quiz - 85%', time: '2 hours ago', icon: Target, color: 'green' },
    { action: 'Started', detail: 'Neurology Practice Session', time: '5 hours ago', icon: Play, color: 'blue' },
    { action: 'Reviewed', detail: '15 Pharmacology Questions', time: 'Yesterday', icon: FileText, color: 'purple' },
    { action: 'Achieved', detail: '7-day Consistency!', time: 'Yesterday', icon: Trophy, color: 'orange' },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Welcome Section */}
        <div className="mb-3 pl-2">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">
            Welcome back, {getFirstName()}!
          </h1>
          <p className="text-slate-500">
            {(() => { const d = new Date(); const day = d.toLocaleDateString('en-AU', { weekday: 'long' }); const dd = d.getDate(); const month = d.toLocaleDateString('en-AU', { month: 'long' }); const yyyy = d.getFullYear(); return `${day}, ${dd} of ${month}, ${yyyy}`; })()}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3">
          {/* Questions Answered */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={BookOpen} colour="blue" size="md" />
              <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-emerald-600">
                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
                12%
              </div>
            </div>
            <div className="text-xl sm:text-3xl font-bold text-slate-900 mb-1">{stats.questionsAnswered}</div>
            <div className="text-xs sm:text-sm text-slate-500">Questions Answered</div>
          </div>

          {/* Accuracy */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Target} colour="green" size="md" />
              <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-emerald-600">
                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
                5%
              </div>
            </div>
            <div className="text-xl sm:text-3xl font-bold text-slate-900 mb-1">{stats.accuracy}%</div>
            <div className="text-xs sm:text-sm text-slate-500">Overall Accuracy</div>
          </div>

          {/* Consistency */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Trophy} colour="amber" size="md" />
              <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-emerald-600">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                Active
              </div>
            </div>
            <div className="text-xl sm:text-3xl font-bold text-slate-900 mb-1">{stats.streak}</div>
            <div className="text-xs sm:text-sm text-slate-500">Consistency</div>
          </div>

          {/* Topics Mastered */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Award} colour="turquoise" size="md" />
              <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-emerald-600">
                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
                2
              </div>
            </div>
            <div className="text-xl sm:text-3xl font-bold text-slate-900 mb-1">{stats.topicsMastered}</div>
            <div className="text-xs sm:text-sm text-slate-500">Topics Mastered</div>
          </div>
        </div>

        {/* Diagnostic Banner */}
        {diagnosticProgress && !diagnosticProgress.all_complete && (
          <div
            className="mb-3 rounded-2xl p-5 cursor-pointer transition-all hover:opacity-95"
            style={
              diagnosticProgress.tests_completed?.length > 0
                ? { background: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)' }
                : { background: 'linear-gradient(135deg, #0a192f 0%, #164e63 100%)' }
            }
            onClick={() => navigate('/diagnostic')}
          >
            <div className="flex items-center justify-between gap-4 text-white">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Icon3D icon={ClipboardList} colour="turquoise" size="sm" />
                </div>
                <div>
                  {diagnosticProgress.tests_completed?.length > 0 ? (
                    <>
                      <p className="font-bold text-sm">
                        Diagnostic Test {diagnosticProgress.current_test} of 3 in progress
                      </p>
                      <p className="text-amber-100 text-xs mt-0.5">
                        Continue where you left off to activate your personalised learning engine
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm">
                        Start your Diagnostic Assessment
                      </p>
                      <p className="text-cyan-200 text-xs mt-0.5">
                        3 tests · 110 questions each · unlock personalised adaptive learning
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {diagnosticProgress.tests_completed?.length > 0 && (
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full font-medium">
                    {diagnosticProgress.tests_completed.length}/3 complete
                  </span>
                )}
                <ChevronRight className="w-5 h-5 opacity-70" />
              </div>
            </div>
          </div>
        )}

        {diagnosticProgress?.all_complete && (
          <div className="mb-3 rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 60%, #0e7490 100%)', boxShadow: 'var(--shadow-card)' }}>
            {/* Header row */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <Icon3D icon={Brain} colour="turquoise" size="sm" />
                <div>
                  <p className="font-bold text-white text-sm">Personalised learning active</p>
                  <p className="text-blue-200 text-xs mt-0.5">
                    Your adaptive engine is choosing every question specifically for you
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/questions')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                Practice Now
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Pre vs Post Diagnostic Scores */}
            {prePostData && (
              <>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mb-4">
                  <div className="rounded-xl p-2 sm:p-3 text-center" style={{ background: 'rgba(255,255,255,0.12)', boxShadow: '0 4px 12px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                    <p className="text-[10px] sm:text-xs font-medium text-blue-200 mb-1">Before Diagnostic</p>
                    <p className="text-lg sm:text-2xl font-bold text-white">{prePostData.pre_accuracy_pct}%</p>
                    <p className="text-[10px] sm:text-xs font-medium text-blue-300">{prePostData.pre_questions} questions</p>
                  </div>
                  <div className="rounded-xl p-2 sm:p-3 text-center" style={{ background: 'rgba(16, 185, 129, 0.2)', boxShadow: '0 4px 12px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                    <p className="text-[10px] sm:text-xs font-medium text-emerald-300 mb-1">After Adaptive</p>
                    <p className="text-lg sm:text-2xl font-bold text-emerald-300">
                      {prePostData.post_accuracy_pct != null ? `${prePostData.post_accuracy_pct}%` : '—'}
                    </p>
                    <p className="text-[10px] sm:text-xs font-medium text-emerald-400">
                      {prePostData.post_questions > 0 ? `${prePostData.post_questions} questions` : 'Start practising!'}
                    </p>
                  </div>
                  <div className="rounded-xl p-2 sm:p-3 text-center" style={{ background: 'rgba(6, 182, 212, 0.2)', boxShadow: '0 4px 12px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15)' }}>
                    <p className="text-[10px] sm:text-xs font-medium text-cyan-300 mb-1">Improvement</p>
                    <div className="flex items-center justify-center gap-1">
                      {prePostData.overall_improvement != null && prePostData.overall_improvement > 0 && (
                        <ArrowUpRight className="w-5 h-5 text-cyan-300" />
                      )}
                      <p className="text-2xl font-bold text-cyan-200">
                        {prePostData.overall_improvement != null
                          ? `${prePostData.overall_improvement > 0 ? '+' : ''}${prePostData.overall_improvement}%`
                          : '—'}
                      </p>
                    </div>
                    <p className="text-xs font-medium text-cyan-400">overall</p>
                  </div>
                </div>

                {/* Top 3 Category Improvements */}
                {prePostData.top_improvements?.length > 0 && (
                  <div className="border-t border-white/15 pt-3">
                    <p className="text-xs font-bold text-cyan-300 mb-2">Your biggest improvements</p>
                    <div className="space-y-1.5">
                      {prePostData.top_improvements.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-white font-medium capitalize truncate mr-3">{item.category}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs font-medium text-blue-300">{item.pre_pct}%</span>
                            <ChevronRight className="w-3 h-3 text-blue-400" />
                            <span className="text-sm font-bold text-emerald-300">{item.post_pct}%</span>
                            <span className="text-xs font-bold text-emerald-300 bg-emerald-500/20 px-1.5 py-0.5 rounded">
                              +{item.improvement}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Main Grid - stretch to align with footer */}
        <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 pb-0" style={{ minHeight: 'calc(100vh - 72px - 250px - 24px)' }}>
          {/* Left Column - 2/3 */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                <h2 className="text-lg font-bold text-slate-900">Quick Actions</h2>
              </div>
              <div className="p-3 sm:p-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(action.path)}
                      className="flex flex-col items-center p-3 sm:p-4 rounded-xl border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/50 transition-all group"
                    >
                      <div className="mb-2 sm:mb-3">
                        <Icon3D icon={action.icon} colour={action.colour} size="md" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-slate-700 text-center">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Topic Progress */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex-1" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                <h2 className="text-lg font-bold text-slate-900">Topic Progress</h2>
                <button
                  onClick={() => navigate('/analytics')}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {topicProgress.length === 0 ? (
                  <div className="text-center py-6">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-400">Answer some questions to see your strengths</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4 text-xs text-slate-400 mb-1">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Strong (≥70%)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Building (50–69%)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Focus needed (&lt;50%)</span>
                    </div>
                    {topicProgress.map((topic, i) => (
                      <div key={i}>
                        <div className="flex justify-between items-center mb-1.5">
                          <span className="text-sm font-medium text-slate-700 capitalize">{topic.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{topic.correct}/{topic.attempted} correct</span>
                            <span className="text-sm font-bold" style={{ color: topic.color }}>{topic.accuracy}%</span>
                          </div>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all relative overflow-hidden"
                            style={{ width: `${Math.max(topic.accuracy, 2)}%`, backgroundColor: topic.color }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" style={{ animation: 'shimmer 3s infinite' }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Load Sample Data - Turquoise */}
            <div className="relative overflow-hidden rounded-2xl p-4 sm:p-6 text-white mt-auto" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative z-10">
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1">New to {tenantName}?</h3>
                  <p className="text-cyan-100 text-sm">Load sample questions to start practicing immediately</p>
                </div>
                <button
                  onClick={handleInitSampleData}
                  disabled={loading}
                  className="px-5 py-2.5 bg-white text-cyan-600 font-semibold rounded-xl hover:bg-cyan-50 transition-colors whitespace-nowrap flex-shrink-0"
                  style={{ boxShadow: 'var(--shadow-3d-btn)' }}
                >
                  {loading ? 'Loading...' : 'Load Sample Questions'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - 1/3 */}
          <div className="flex flex-col h-full justify-between">
            {/* Subscription Status - Turquoise */}
            <div className="relative overflow-hidden rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Icon3D icon={Trophy} colour="amber" size="sm" />
                  <div>
                    <p className="text-sm text-cyan-100">Current Plan</p>
                    <p className="font-bold">
                      {parsePlan(subscription?.subscription_plan).display} Plan
                    </p>
                  </div>
                </div>
                {(!subscription?.subscription_plan || subscription?.subscription_plan === 'free') && (
                  <button
                    onClick={() => navigate('/subscription')}
                    className="w-full py-2.5 bg-white text-cyan-600 font-semibold rounded-xl hover:bg-cyan-50 transition-all"
                  >
                    Upgrade Now
                  </button>
                )}
              </div>
            </div>

            {/* Improvement Potential Card — visible when diagnostic is complete */}
            {improvementPredictions && improvementPredictions.__overall__ && (
              <div className="rounded-2xl p-5 text-white mt-6" style={{ background: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 60%, #0e7490 100%)', boxShadow: 'var(--shadow-card)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon3D icon={TrendingUp} colour="turquoise" size="sm" />
                  <div>
                    <p className="font-bold text-sm leading-tight">Your Improvement Potential</p>
                    <p className="text-blue-200 text-xs">How much you could improve with consistent practice</p>
                  </div>
                </div>

                {/* 3 time-period boxes — show potential gain, NOT absolute targets */}
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-4">
                  {[
                    { label: '1 Week', gain: improvementPredictions.__overall__.potential_gain_1_week },
                    { label: '1 Month', gain: improvementPredictions.__overall__.potential_gain_1_month },
                    { label: '3 Months', gain: improvementPredictions.__overall__.potential_gain_3_months },
                  ].map((period, i) => (
                    <div key={i} className="bg-white/10 rounded-xl p-2 sm:p-2.5 text-center border border-white/10">
                      <p className="text-[10px] sm:text-xs text-blue-200 mb-1">{period.label}</p>
                      <p className="text-base sm:text-lg font-bold text-emerald-300">+{period.gain?.toFixed(1)}%</p>
                      <p className="text-[10px] sm:text-xs text-blue-300">potential gain</p>
                    </div>
                  ))}
                </div>

                {/* Top 3 categories by mark impact or growth potential */}
                {(() => {
                  const cats = Object.entries(improvementPredictions)
                    .filter(([k]) => k !== '__overall__')
                    .sort((a, b) => (a[1].priority_rank || 99) - (b[1].priority_rank || 99))
                    .slice(0, 3);
                  return cats.length > 0 ? (
                    <div className="mb-3">
                      <p className="text-xs text-blue-300 font-medium mb-2">Focus areas for maximum impact:</p>
                      <div className="space-y-1.5">
                        {cats.map(([cat, pred]) => (
                          <div key={cat} className="flex items-center justify-between text-xs">
                            <span className="text-blue-100 capitalize truncate mr-2">{cat}</span>
                            <span className="text-emerald-300 font-semibold flex-shrink-0">
                              could gain +{pred.potential_gain_3_months?.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                <p className="text-xs text-blue-300 leading-relaxed border-t border-white/10 pt-3">
                  These are potential improvements, not guarantees. Consistent effort makes the difference.
                </p>
              </div>
            )}

            {/* Recent Activity - Centered between Current Plan and Consistency */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex-1 mt-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Recent Activity</h2>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {recentActivity.map((activity, i) => {
                    const Icon = activity.icon;
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                        <div className="flex-shrink-0">
                          <Icon3D
                            icon={Icon}
                            colour={
                              activity.color === 'green' ? 'green' :
                              activity.color === 'blue' ? 'turquoise' :
                              activity.color === 'purple' ? 'purple' :
                              'amber'
                            }
                            size="sm"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{activity.action}</p>
                          <p className="text-xs text-slate-500 truncate">{activity.detail}</p>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">{activity.time}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Consistency Card */}
            <div className="rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
              <div className="flex items-center gap-3 mb-3">
                <Icon3D icon={Trophy} colour="amber" size="md" />
                <div>
                  <p className="text-2xl font-bold">{stats.streak} Days</p>
                  <p className="text-cyan-100 text-sm">Consistency</p>
                </div>
              </div>
              <p className="text-sm text-cyan-100">
                Keep studying daily to maintain your consistency and unlock achievements!
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
